import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { faFlag, faSearch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { Filters } from "@edgetier/filters";
import { FEATURE_FLAGS } from "types-for/feature-flags";
import { useFlags } from "hooks-for/feature-flags";
import { useUser } from "hooks-for/users/use-user";
import { useFlagsUtilities } from "@edgetier/feature-flags";
import toast from "react-hot-toast";
import { ToggleSwitch } from "@edgetier/client-components";
import "./feature-flag-toggler.scss";
import { Tooltip } from "@edgetier/components";
import { formatFeatureFlagForList, getSortedFeatureFlagsList, updateFlagState } from "./feature-flag-toggler.utilities";
import { COMPONENT_DATA_TEST_ID } from "./feature-flag-toggler.constants";

/**
 * Component for toggling feature flags in a modal.
 * Provides a list of feature flags with the ability to search and toggle their states.
 * @returns {JSX.Element} FeatureFlagToggler component.
 */
export const FeatureFlagToggler = () => {
    const [search, setSearch] = useState("");

    const utilities = useFlagsUtilities();
    const flags = useFlags(Object.values(FEATURE_FLAGS));
    const { data: user } = useUser();
    const { openModal, modalProps } = useModal({ canDismiss: false });

    const featureFlagsList = useMemo(() => {
        return getSortedFeatureFlagsList().map((flagKey) =>
            formatFeatureFlagForList({ flagKey, flagEnabled: flags[flagKey].enabled })
        );
    }, [flags]);

    const toggleFeatureFlag = useCallback(
        async (flagKey: string, state: boolean) => {
            if (user) {
                try {
                    await updateFlagState({ flagKey, state, user });
                    utilities.getFlags();
                    toast.success("Feature flag updated successfuly. Updating local flags.");
                } catch {
                    toast.error("Failed to update feature flag");
                }
            }
        },
        [user, utilities]
    );

    const filteredFlags = useMemo(() => {
        return featureFlagsList.filter((flag) => flag.name.toLowerCase().includes(search.toLowerCase()));
    }, [featureFlagsList, search]);

    return (
        <>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Feature Flags Toggler</h2>
                </ModalHeader>
                <ModalContent>
                    <div data-testid={`${COMPONENT_DATA_TEST_ID}__modal-content`}>
                        <Filters
                            initialValues={{ search: "" }}
                            visibleFilters={["search"]}
                            fixedFilters={["search"]}
                            submitOnChange
                            onSubmit={({ search }) => setSearch(search)}
                            filters={[
                                {
                                    id: "search",
                                    type: "search",
                                    icon: faSearch,
                                    label: "",
                                    placeholder: "Search for users",
                                    name: "search",
                                    fieldType: "text",
                                    maxLength: 300,
                                    autocomplete: false,
                                },
                            ]}
                        />
                        <div
                            data-testid={`${COMPONENT_DATA_TEST_ID}__flags-list`}
                            className="feature-flags-toggler__list"
                        >
                            {filteredFlags.length === 0 && <p>No feature flags found</p>}
                            {filteredFlags.map((item, index) => (
                                <div
                                    data-testid={`${COMPONENT_DATA_TEST_ID}__flag-item`}
                                    key={`list-item-${index}`}
                                    className="feature-flags-toggler__list-item"
                                >
                                    <div className="feature-flags-toggler__item-description">
                                        <span className="feature-flags-toggler__flag-label">{item.label}</span>
                                        <span className="feature-flags-toggler__flag-key">{item.name}</span>
                                    </div>
                                    <Tooltip
                                        content="Trust me, you don't want to disable this one"
                                        disableTooltip={item.name !== FEATURE_FLAGS.InternalFeatureFlagToggler}
                                        useArrow
                                    >
                                        <ToggleSwitch
                                            data-testid={`${COMPONENT_DATA_TEST_ID}__switch-${item.name}`}
                                            checked={item.enabled}
                                            onChange={(enabled) => toggleFeatureFlag(item.name, enabled)}
                                            disabled={item.name === FEATURE_FLAGS.InternalFeatureFlagToggler}
                                        />
                                    </Tooltip>
                                </div>
                            ))}
                        </div>
                    </div>
                </ModalContent>
            </Modal>
            <li
                data-testid={`${COMPONENT_DATA_TEST_ID}__trigger`}
                className="feature-flags-toggler navigation-link"
                onClick={openModal}
            >
                <div className="navigation-link__inner">
                    <FontAwesomeIcon icon={faFlag} />
                    Feature Flag Toggler
                </div>
            </li>
        </>
    );
};
